/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useReducer } from 'react'
import { useIsMounted } from './use-is-mounted'

/**
 * A hook to make it more convinient to fetch data from remote endpoint
 *
 * Demo usage:
 *
 * ```
 * function fetchDataFn(params: {foo: 'bar'}) {
 *   return getApi('/api/ghg/v1/resource', params);
 * }
 *
 * const [res, fetchData, reset] = useAsync(null as Models.Something | null, fetchDataFn);
 *
 * useEffect(() => {
 *   fetchData()
 * }, [fetchData])
 *
 * return res.loading ? 'Loading ...' : <pre>{JSON.stringify(res.data)}</pre>
 * ```
 */

export interface UseAsyncState<T> {
  loading: boolean
  error?: any
  data: null | T
  lastFetched: null | Date
}

type UseAsyncAction<T> =
  | { type: 'ASYNC_START' }
  | { type: 'ASYNC_COMPLETE'; data: T }
  | { type: 'ASYNC_ERROR'; error: any }
  | { type: 'RESET' }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stateReducer = <T>(state: UseAsyncState<T>, action: UseAsyncAction<T>): UseAsyncState<T> => {
  switch (action.type) {
    case 'ASYNC_START':
      return {
        ...state,
        loading: true,
        error: null,
        data: null,
      }
    case 'ASYNC_COMPLETE':
      return {
        ...state,
        loading: false,
        error: null,
        data: action.data,
        lastFetched: new Date(),
      }
    case 'ASYNC_ERROR':
      return {
        ...state,
        loading: false,
        data: null,
        error: action.error,
      }
    case 'RESET':
      return {
        ...state,
        loading: false,
        data: null,
        error: null,
        lastFetched: null,
      }
    default:
      throw new Error()
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useAsync = <
  T extends (...args: any[]) => Promise<any>,
  E = ReturnType<T> extends Promise<infer U> ? U : never
>(
  asyncFunction: T
): [UseAsyncState<E>, (...funcArgs: Parameters<T>) => Promise<E | null>, () => void] => {
  const isMounted = useIsMounted()

  const [state, dispatch] = useReducer<
    (state: UseAsyncState<E>, action: UseAsyncAction<E>) => UseAsyncState<E>
  >(stateReducer, {
    loading: false,
    error: false,
    data: null,
    lastFetched: null,
  })

  const execute = useCallback(
    async (...args: Parameters<T>): Promise<E | null> => {
      dispatch({ type: 'ASYNC_START' })

      try {
        const data = await asyncFunction(...args)
        if (isMounted()) {
          dispatch({ type: 'ASYNC_COMPLETE', data })
        }
        return data
      } catch (error) {
        if (isMounted()) {
          dispatch({ type: 'ASYNC_ERROR', error })
        }
        throw error
      }
    },
    [isMounted, asyncFunction]
  )

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return [state, execute, reset]
}
